import { Box, Button, Grid, Typography } from '@mui/material';
import ResourceAPI from 'api/ResourceAPI';
import UserApiService from 'api/UserApiService';
import { Layout, Loader } from 'components';
import { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import useApi from 'hooks/useApi';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import Order from 'types/entities/Order';
import TMOrderScope from 'types/entities/TMOrderScope';
import Affair from 'types/entities/Affair';
import User from 'types/entities/User';
import Scope from 'types/entities/Scope';
import ScopeApiService from 'api/ScopeApiService';
import OrderForm from '../OrderForm/OrderForm';
import { OrderType } from 'types/enums/OrderType';
import { updateIds } from 'helpers/orderhelper';
import orderApiService from 'api/OrderApiService';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { formatDateToddMMYYY } from 'helpers/format';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';

interface RouteParams {
  catalog_id: string;
}

const CreationOrder: React.FC = () => {
  const { catalog_id } = useParams<any>();
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const purchaseOrder = useSelector((state) => state.purchaseOrder);
  const loadings = useSelector((state) => state.loadings);
  const [t] = useTranslation();
  const [newOrder, setNewOrder] = useState<Partial<Order>>({
    start_date: moment().format('YYYY-MM-DD'), // moment().toISOString(),
    end_date: moment().format('YYYY-MM-DD'), // moment().toISOString(),
    is_sign_mandatory: true,
    client_connection: appState.customer?.client_connection,
    name_spec: '',
  });
  const history = useHistory();
  const { makeCall } = useApi();
  const customer = useSelector((state) => state.app.customer);
  const [purchaseOrderOTP, setPurchaseOrderOTP] = useState<string | undefined>();
  const dashboard = useSelector((state) => state.dashboard);

  const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    if (e.currentTarget.name === 'draft_reference' && e.currentTarget.value === '') {
      return setNewOrder({ ...newOrder, [e.currentTarget.name]: null });
    }
    return setNewOrder({ ...newOrder, [e.currentTarget.name]: e.currentTarget.value });
  };

  const handleSelectClient = (client: User | null | undefined) => {
    if (client) return setNewOrder({ ...newOrder, client, client_id: client.id });
    return setNewOrder({ ...newOrder, client: undefined, client_id: undefined });
  };
  const handleSelectRepresentative = (representative: User | null | undefined) => {
    if (representative) return setNewOrder({ ...newOrder, delegue: representative, delegue_id: representative.id });
    return setNewOrder({ ...newOrder, delegue: undefined, delegue_id: undefined });
  };
  const handleSelectOptions = (e: SelectTypeOptions | null, property: PropertyNameOptions) => {
    if (e && property === 'affair') {
      setPurchaseOrderOTP((e as any)?.otp);
      return setNewOrder({ ...newOrder, [`${property}`]: e, [`${property}_id`]: e.id });
    }
    return setNewOrder({ ...newOrder, affair: undefined, affair_id: undefined });
  };
  // const handleDateChange = (name: string, date: Moment | null) =>
  //   setNewOrder({ ...newOrder, [name]: moment(date).format('YYYY-MM-DD') });
  // setNewOrder({ ...newOrder, [name]: date?.toISOString() });

  const handleDateChange = (name: string, date: Moment | null) => {
    // if (!date) {
    //   setNewOrder({ ...newOrder, [name]: null });
    //   return;
    // }

    const formattedDate = date?.format('YYYY-MM-DD');
    setNewOrder({ ...newOrder, [name]: formattedDate });
  };

  const [isInValidation, setIsInValidation] = useState(false);
  const [isMissingInputs, setIsMissingInputs] = useState(false);
  const [creators, setCreators] = useState<User[]>([]);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<Scope[]>([]);
  const [archivedScopes, setArchivedScopes] = useState<Scope[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [consultants, setConsultants] = useState<User[]>([]);
  const [archivedConsultants, setArchivedConsultants] = useState<User[]>([]);
  const [selectedConsultants, setSelectedConsultants] = useState<User[]>([]);
  const [deliveryManagers, setDeliveryManagers] = useState<User[]>([]);
  const [archivedDeliveryManagers, setArchivedDeliveryManagers] = useState<User[]>([]);
  const [selectedDeliveryManager, setSelectedDeliveryManager] = useState<User[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [archivedRepresentatives, setArchivedRepresentatives] = useState<User[]>([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState<User[]>([]);
  const userLocale = navigator.language || 'fr-FR';

  useEffect(() => {
    const getScopes = async () => {
      if (customer?.id) {
        const scopesRetrieved = await ScopeApiService.fetchByCustomerId(customer.id);
        setScopes(scopesRetrieved);
      }
    };
    getScopes();
    dispatch(getPurchaseOrderAction([]));
  }, []);

  useEffect(() => {
    const getRepresentatives = async () => {
      const creatorsFetched = await UserApiService.fetchPossiblesDelegues();
      setRepresentatives(creatorsFetched);
    };
    getRepresentatives();
  }, []);

  useEffect(() => {
    const getPurchaseOrder = async () => {
      if (purchaseOrderOTP) {
        dispatch(addLoadingAction('purchaseorderlist'));
        const purchaseOrderList = await orderApiService.fetchPurchaseOrder(purchaseOrderOTP);
        purchaseOrderList.forEach(
          (po: {
            poend: string;
            poref: string;
            reference: string;
            postart: string;
            is_valid: boolean;
            is_archived: boolean;
          }) => {
            po.is_valid = false; // Set is_valid to false for all items
            po.is_archived = true;
            po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
          }
        );
        dispatch(getPurchaseOrderAction(purchaseOrderList));
        dispatch(removeLoadingAction('purchaseorderlist'));
      }
    };
    getPurchaseOrder();
  }, [purchaseOrderOTP]);

  const isDateValid = (startDate: string | undefined, endDate: string | undefined): boolean => {
    if (!startDate || !endDate) {
      return false;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }

    return end >= start;
  };

  useEffect(() => {
    setIsMissingInputs(
      // moment(newOrder?.end_date).isBefore(newOrder?.start_date) ||
      selectedScopes.length === 0 ||
      selectedDeliveryManager.length === 0 ||
      selectedConsultants.length === 0 ||
      newOrder.name_spec?.length === 0 ||
        !isDateValid(newOrder?.start_date, newOrder?.end_date)
    );
  }, [newOrder, selectedScopes, selectedDeliveryManager, selectedConsultants]);

  const handleSubmitNewOrder = async () => {
    setIsInValidation(true);
    const { affair, client, ...orderToSave } = newOrder as Order;

    const { ids: accountable_ids, archived: is_accountable_archived } = updateIds(
      selectedDeliveryManager,
      archivedDeliveryManagers
    );

    const { ids: consultant_ids, archived: is_consultant_archived } = updateIds(
      selectedConsultants,
      archivedConsultants
    );

    const { ids: representative_ids, archived: is_representative_archived } = updateIds(
      selectedRepresentative,
      archivedRepresentatives
    );

    const { ids: scope_ids, archived: is_scope_archived } = updateIds(selectedScopes, archivedScopes);

    const client_ids = client?.id ? [client?.id] : null;
    const is_client_archived = client_ids !== null ? [false] : [];

    const tmOrderScope: TMOrderScope = {
      id: 0, // This will be generated by the backend
      accountable_ids,
      consultant_ids,
      delegue_ids: representative_ids,
      client_ids,
      scope_ids,
      order_id: orderToSave.id,
      is_scope_archived,
      is_accountable_archived,
      is_consultant_archived,
      is_client_archived,
      is_delegue_archived: is_representative_archived,
    };

    try {
      const res = await ResourceAPI.post('orders', {
        ...orderToSave,
        client_id: newOrder?.client_id,
        catalog_id: catalog_id as string,
        customer_id: appState.customer?.id,
        affair_name: affair?.name || null,
        affair_otp: affair?.otp || null,
        affair_id: affair?.id || null,
        creator_id: appState.user?.id,
        order_type_id: OrderType.CATALOG,
        tm_order_scope: tmOrderScope,
        purchase_orders: purchaseOrder?.datas,
        action_id: ActionDescriptor.CreateCatalogOrder,
      });
      if (res) {
        history.push(`/${appState.customer?.slug}/catalog/${catalog_id as string}/order/${res.data.order.id}`);
      }
      dispatch(getPurchaseOrderAction([]));
    } catch (error) {
      const result = (error as Error).message;

      if (result) {
        dispatch(
          setSnackbarAction({
            message: `Fetch order-workunits from active catalog failed : ${result}`,
            open: true,
            severity: 'error',
          })
        );
      } else {
        dispatch(
          setSnackbarAction({
            message: `Fetch order-workunits from active catalog failed : ${error}`,
            open: true,
            severity: 'error',
          })
        );
      }
    } finally {
      setIsInValidation(false);
      dispatch(getPurchaseOrderAction([]));
    }
  };
  const handleSwitchSign = (value: boolean) => {
    setNewOrder({ ...newOrder, is_sign_mandatory: value });
  };

  const handleClientConnectionSwitch = (value: boolean) => {
    setNewOrder({ ...newOrder, client_connection: value });
  };

  const handleScopeAdded = (newScope: Scope) => {
    const scopeWithArchiveStatus = { ...newScope, is_archived: false };
    setScopes((prevScopes) => [...prevScopes, scopeWithArchiveStatus]);
  };

  const handleScopeArchived = (updatedScope: Scope) => {
    setScopes((prevScopes) =>
      prevScopes.map((scope) => (scope.id === updatedScope.id ? { ...scope, is_archived: true } : scope))
    );
    setArchivedScopes((prevArchivedScopes) => [...prevArchivedScopes, updatedScope]);
  };

  const handleScopesChange = (newSelectedScopes: Scope[]) => {
    setSelectedScopes(newSelectedScopes);
  };

  const handleConsultantChange = (newSelectedConsultants: User[], reason?: string) => {
    setSelectedConsultants(newSelectedConsultants);
  };

  const handleConsultantArchived = (updatedUser: User) => {
    setArchivedConsultants((prevArchiveConsultant) => [...prevArchiveConsultant, updatedUser]);
  };

  const handleDeliveryManagerChange = (newSelectedDeliveryManager: User[], reason?: string) => {
    setSelectedDeliveryManager(newSelectedDeliveryManager);
  };

  const handleRepresentativeChange = (newSelectedRepresentative: User[], reason?: string) => {
    setSelectedRepresentative(newSelectedRepresentative);
  };

  const handleDeliveryManagerArchived = (updatedUser: User) => {
    setArchivedDeliveryManagers((prevArchivedDeliveryManager) => [...prevArchivedDeliveryManager, updatedUser]);
  };

  const handleRepresentativeArchived = (updatedUser: User) => {
    setArchivedRepresentatives((prevArchiveRepresentative) => [...prevArchiveRepresentative, updatedUser]);
  };

  useEffect(() => {
    const getClients = async () => {
      if (appState.customer) {
        const res = await makeCall(
          UserApiService.getClientsOnCustomer(appState.customer?.id, { is_archived: 0, size: '-1' }),
          'Failed to retrieve clients'
        );
        setClients(res?.datas || []);
      }
    };

    getClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.customer]);

  useEffect(() => {
    const getConsultants = async () => {
      if (appState.customer && appState.customer.id) {
        const res = await makeCall(UserApiService.fetchPossiblesConsultants(), 'Unable to retrieve consultants');
        setConsultants(res);
      }
    };
    getConsultants();
  }, [appState.customer]);

  useEffect(() => {
    const getDeliveryManagers = async () => {
      if (appState.customer && appState.customer.id) {
        const res = await makeCall(
          UserApiService.fetchPossiblesDeliverableTeamLeaders(),
          'Unable to retrieve delivery managers'
        );
        setDeliveryManagers(res);
      }
    };
    getDeliveryManagers();
  }, [appState.customer]);

  const filterAffairs = (name?: string, otp?: string): Affair | undefined => {
    if (!appState?.affairs?.length) return undefined;
    if (otp) {
      const foundByOtp = appState.affairs.find((a) => String(a.otp) === String(otp));
      if (foundByOtp) return foundByOtp;
    }
    if (!name) return undefined;
    const searchName = name.trim().toLowerCase();
    return (
      appState.affairs.find((a) => a.name?.trim().toLowerCase() === searchName) ??
      appState.affairs.find((a) => a.name?.toLowerCase().includes(searchName))
    );
  };

  useEffect(() => {
    if (!dashboard.todoOrderCreateList) return;
    const matchedAffair = filterAffairs(
      dashboard.todoOrderCreateList.engagement_name,
      dashboard.todoOrderCreateList.otp
    );
    setNewOrder((prev) => ({
      ...prev,
      affair: matchedAffair,
      affair_id: matchedAffair?.id ?? dashboard.todoOrderCreateList?.otp,
    }));
  }, [dashboard.todoOrderCreateList, appState.affairs]);

  if (loadings?.todocreateorder) {
    return <Loader size={20} className="white-loader" />;
  }

  return (
    <Layout name={t('Create_New_Order')} path="Create_New_Order">
      {loadings?.todocreateorder ? (
        <Box sx={{ width: '80px', alignSelf: 'flex-start' }}>
          <Loader size={20} className="white-loader" />
        </Box>
      ) : (
        <>
          <OrderForm
            handleChange={handleChange}
            handleSelectClient={handleSelectClient}
            handleSelectOptions={handleSelectOptions}
            handleDateChange={handleDateChange}
            handleSwitchSign={handleSwitchSign}
            handleClientConnectionSwitch={handleClientConnectionSwitch}
            order={newOrder}
            representatives={representatives}
            clients={clients}
            scopes={scopes}
            consultants={consultants}
            deliveryManagers={deliveryManagers}
            onScopeAdded={handleScopeAdded}
            onScopeArchived={handleScopeArchived}
            handleScopesChange={handleScopesChange}
            onConsultantArchived={handleConsultantArchived}
            onRepresentativeArchived={handleRepresentativeArchived}
            handleConsultantChange={handleConsultantChange}
            handleRepresentativeChange={handleRepresentativeChange}
            onDeliveryManagerArchived={handleDeliveryManagerArchived}
            handleDeliveryManagerChange={handleDeliveryManagerChange}
            selectedScopes={selectedScopes}
            selectedConsultants={selectedConsultants}
            selectedDeliveryManager={selectedDeliveryManager}
            selectedRepresentative={selectedRepresentative}
            variant="filled"
          />
          <Box
            sx={{
              justifyContent: 'right',
              display: 'flex',
            }}
          >
            <Button
              onClick={() => handleSubmitNewOrder()}
              disabled={isInValidation || isMissingInputs}
              color="success"
              variant="contained"
            >
              {isInValidation ? <Loader size={20} className="white-loader" /> : t('Validate')}
            </Button>
          </Box>
        </>
      )}
    </Layout>
  );
};

export default CreationOrder;
